import * as React from 'react';
import { IStateResponse } from '../../interfaces/state';
import ePharmaServices from '../../services/epharma-services';
import { IProductResponse } from '../../interfaces/product';
import { ISubProductResponse } from '../../interfaces/subProducts';
import { IBeneficiaryCouponRequest, IBeneficiaryCouponUpdateRequest, IBeneficiaryRequest } from '../../interfaces/beneficiary';
import ConfigService from '../../services/config-service';
import keys from '../../util/keys';

export default function CouponRegisterHook()
{
    const [ states, setStates ] = React.useState<IStateResponse[]>([] as IStateResponse[]);
    const [ products, setProducts ] = React.useState<IProductResponse[]>([] as IProductResponse[]);
    const [ subProducts, setSubProducts ] = React.useState<ISubProductResponse[]>([] as ISubProductResponse[]);
    const [ loadingProducts, setLoadingProducts ] = React.useState(false);
    const [ generalBenefit, setBenefitId ] = React.useState(0);

    const getPrescriber = async (stateCode: string, crm: string) =>
    {
        const baseUrl = await ConfigService().getConfig(keys.ePharmaCommonService);
        const prescriberUrl = await ConfigService().getConfig(keys.EndpointCommonPrescriber);

        const gridParams = {
            page: 1,
            itemsPerPage: 1,
            filter: [
                {
                    operator: "=",
                    property: "clientSystemSg",
                    value: stateCode
                },
                {
                    operator: "=",
                    property: "clientSystemNumber",
                    value: crm
                }
            ],
            sorter: []
        };

        const response = await ePharmaServices(baseUrl).get(`${prescriberUrl}/?gridParams=${encodeURIComponent(JSON.stringify(gridParams))}`);

        return response.data.data != undefined && response.data.data[0] != null;
    }

    const getState = async () =>
    {
        const baseUrl = await ConfigService().getConfig(keys.ePharmaCommonService);
        const stateUrl = await ConfigService().getConfig(keys.EndpointCommonState);

        const gridParams = {
            page: 1,
            itemsPerPage: 30,
            filter: [],
            sorter: []
        };

        const response = await ePharmaServices(baseUrl).get(`${stateUrl}?gridParams=${encodeURIComponent(JSON.stringify(gridParams))}`);

        setStates(response.data.data);
    }

    const getBenefit = async () =>
    {
        const benefitId = await ConfigService().getConfig(keys.GeneralBenefit);
        setBenefitId(benefitId);
    }

    const getProducts = async (coupon: string) =>
    {
        setLoadingProducts(true);

        const baseUrl = window.eurofarmaService.base;
        const productUrl = await ConfigService().getConfig(keys.EndPointEurofarmaProduct);

        const response = await ePharmaServices(baseUrl).get(`${productUrl.replace("{coupom}", coupon)}`);

        setProducts(response.data.data || []);
        setLoadingProducts(false);
    }

    const getSku = async (product: string) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const productUrl = await ConfigService().getConfig(keys.EndPointEurofarmaSubProduct);

        const response = await ePharmaServices(baseUrl).get(`${productUrl.replace("{product}", product)}`);

        setSubProducts(response.data.data || []);
    }

    const verifyCoupom = async (coupon: string) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const verifyCoupomUrl = await ConfigService().getConfig(keys.EndpointEurofarmaVerifyCoupon);

        const response = await ePharmaServices(baseUrl).get(`${verifyCoupomUrl.replace("{coupom}", coupon)}`);

        return response.data;
    }

    const sendBeneficiaryCoupon = async (request: IBeneficiaryCouponRequest | IBeneficiaryCouponUpdateRequest) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const beneficiaryCupomUrl = (request.beneficiaryId == 0)
            ? await ConfigService().getConfig(keys.EndPointEurofarmaBeneficiaryCupom)
            : await ConfigService().getConfig(keys.EndPointBeneficiaryEditCupom);

        if (request.crmState == "RJ")
            request.crmNumber = "52" + request.crmNumber;

        request.cpf = request.cpf.replace(".", "")
            .replace(".", "")
            .replace("-", "");

        const { beneficiaryId, ...restRequest } = request;

        const response = (request.beneficiaryId == 0)
            ? await ePharmaServices(baseUrl).post(`${beneficiaryCupomUrl}`, restRequest)
            : await ePharmaServices(baseUrl).put(`${beneficiaryCupomUrl}`, request);

        return response.data;
    }

    const verifyEmployee = async (cpf: string) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const verifyPerson = await ConfigService().getConfig(keys.EndpointEurofarmaVerifyEmployee);

        const response = await ePharmaServices(baseUrl).get(`${verifyPerson.replace("{cpf}", cpf)}`);

        return response.data;
    }

    const verifyFakePerson = async (cpf: string, bornDate: string) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const verifyPersonFake = await ConfigService().getConfig(keys.EndpointEurofarmaVerifyPerson);

        const url = verifyPersonFake
        .replace("{cpf}", cpf)
        .replace("{bornDate}", bornDate);

        const response = await ePharmaServices(baseUrl).get(url);

        return response.data;
    }

    const sendBeneficiaryCpf = async (request: IBeneficiaryCouponRequest | IBeneficiaryCouponUpdateRequest) =>
    {
        const baseUrl = window.eurofarmaService.base;
        const beneficiaryCupomUrl = await ConfigService().getConfig(keys.EndPointEurofarmaBeneficiaryCpf);
        if (request.crmState == "RJ")
            request.crmNumber = "52" + request.crmNumber;

        request.cpf = request.cpf.replace(".", "")
            .replace(".", "")
            .replace("-", "");
        
        request.cpfComprador = request.cpfComprador.replace(".", "")
        .replace(".", "")
        .replace(".", "");

        const { beneficiaryId, ...restRequest } = request;

        const response = await ePharmaServices(baseUrl).put(`${beneficiaryCupomUrl}`, request);

        return response.data;
    }

    const sendBeneficiary = async (request: IBeneficiaryCouponRequest, benefitId: number) =>
    {
        const baseUrl = await ConfigService().getConfig(keys.ePharmaBeneficiaryService);
        const beneficiaryUrl = await ConfigService().getConfig(keys.EndpointBeneficiaryBeneficiary);
        const findBeneficiaryUrl = await ConfigService().getConfig(keys.EndPointBeneficiaryFindBeneficiary)

        const result = await ePharmaServices(baseUrl).get(findBeneficiaryUrl.replace("{benefitId}", benefitId)
            .replace("{identifier}", request.cpf.replace(".", "")
                .replace(".", "")
                .replace("-", "")));

        if (!result.data.success)
        {
            const splitDate = request.bornDate.split("/");
            const bornDate = new Date(parseInt(splitDate[2]), parseInt(splitDate[1]), parseInt(splitDate[0]));

            const beneficiaryRequest = {
                benefitId: benefitId,
                cpf: request.cpf.replace(".", "")
                    .replace(".", "")
                    .replace("-", ""),
                cardholderNumber: request.cpf.replace(".", "")
                    .replace(".", "")
                    .replace("-", ""),
                mainCardholderNumber: request.cpf.replace(".", "")
                    .replace(".", "")
                    .replace("-", ""),
                name: request.cpf,
                birthDate: bornDate.toLocaleDateString('en-US'),
                startDate: new Date().toLocaleDateString('en-US')
            } as IBeneficiaryRequest;

            const response = await ePharmaServices(baseUrl).post(`${beneficiaryUrl}`, beneficiaryRequest);

            return response.data;
        }

        return result.data;
    }



    const getBeneficiary = async (cpf: string, benefitId: number) =>
    {
        const baseUrl = await ConfigService().getConfig(keys.ePharmaBeneficiaryService);
        const beneficiaryUrl = await ConfigService().getConfig(keys.EndPointBeneficiaryFindBeneficiary)


        cpf = cpf.replace(".", "")
                 .replace(".", "")
                 .replace("-", "");

        const result = await ePharmaServices(baseUrl).get(beneficiaryUrl.replace("{benefitId}", benefitId)
                                                                        .replace("{identifier}", cpf));

        return result.data;
    }

    return {
        states, getState,
        generalBenefit, getBenefit,
        products, getProducts,
        subProducts, getSku,
        loadingProducts, setLoadingProducts,
        verifyCoupom, sendBeneficiaryCoupon,
        sendBeneficiary, getBeneficiary,
        getPrescriber, sendBeneficiaryCpf,
        verifyEmployee,
        verifyFakePerson
    };
}